export const PERIOD_PRESETS = ['today', 'last_7_days', 'this_month', 'last_month', 'this_year'] as const;
export type PeriodPreset = (typeof PERIOD_PRESETS)[number];

export const DEFAULT_PERIOD: PeriodPreset = 'last_7_days';

export type SeriesUnit = 'hour' | 'day' | 'week' | 'month';

const SERIES_UNITS: Readonly<Record<PeriodPreset, SeriesUnit>> = {
  today: 'hour',
  last_7_days: 'day',
  this_month: 'day',
  last_month: 'day',
  this_year: 'month',
};

export const seriesUnitOf = (preset: PeriodPreset): SeriesUnit => SERIES_UNITS[preset];

export type ReportPeriod = {
  readonly from: string;
  readonly to: string;
  readonly fromDay: string;
  readonly toDay: string;
};

const DAYS_IN_WEEK = 7;

const startOfDay = (instant: Date): Date =>
  new Date(instant.getFullYear(), instant.getMonth(), instant.getDate());

const startOfMonth = (year: number, month: number): Date => new Date(year, month, 1);

const addDays = (day: Date, days: number): Date =>
  new Date(day.getFullYear(), day.getMonth(), day.getDate() + days);

const twoDigits = (value: number): string => String(value).padStart(2, '0');

export const localDay = (day: Date): string =>
  `${String(day.getFullYear())}-${twoDigits(day.getMonth() + 1)}-${twoDigits(day.getDate())}`;

const periodOf = (from: Date, to: Date): ReportPeriod => ({
  from: from.toISOString(),
  to: to.toISOString(),
  fromDay: localDay(from),
  toDay: localDay(to),
});

const localDateOf = (day: string): Date => {
  const [year = 0, month = 1, date = 1] = day.split('-').map(Number);
  return new Date(year, month - 1, date);
};

export const lastDayOf = (period: ReportPeriod): string => localDay(addDays(localDateOf(period.toDay), -1));

export const recentDaysPeriodOf = (days: number, now: Date): ReportPeriod => {
  const tomorrow = addDays(startOfDay(now), 1);
  return periodOf(addDays(tomorrow, -days), tomorrow);
};

export const dayOrNull = (value: string): string | null => (value === '' ? null : value);

export type DayRange = { readonly fromDay: string; readonly toDay: string };

const DEFAULT_RANGE_DAYS = 31;

const MAX_DAILY_SERIES_DAYS = 14;

const MAX_WEEKLY_SERIES_DAYS = 120;

export const MAX_DAY_BUCKETS = 400;

const MILLISECONDS_PER_DAY = 86_400_000;

export const defaultDayRangeOf = (now: Date): DayRange => {
  const period = recentDaysPeriodOf(DEFAULT_RANGE_DAYS, now);
  return { fromDay: period.fromDay, toDay: lastDayOf(period) };
};

export const dayRangePeriodOf = (range: DayRange): ReportPeriod =>
  periodOf(localDateOf(range.fromDay), addDays(localDateOf(range.toDay), 1));

export const daysIn = (range: DayRange): number =>
  Math.round(
    (localDateOf(range.toDay).getTime() - localDateOf(range.fromDay).getTime()) / MILLISECONDS_PER_DAY,
  ) + 1;

export const seriesUnitOfRange = (range: DayRange): SeriesUnit => {
  const days = daysIn(range);
  if (days <= MAX_DAILY_SERIES_DAYS) return 'day';
  return days <= MAX_WEEKLY_SERIES_DAYS ? 'week' : 'month';
};

export const reportPeriodOf = (preset: PeriodPreset, now: Date): ReportPeriod => {
  const tomorrow = addDays(startOfDay(now), 1);
  if (preset === 'today') return periodOf(startOfDay(now), tomorrow);
  if (preset === 'this_year')
    return periodOf(startOfMonth(now.getFullYear(), 0), startOfMonth(now.getFullYear() + 1, 0));
  if (preset === 'last_7_days') return recentDaysPeriodOf(DAYS_IN_WEEK, now);
  const thisMonth = startOfMonth(now.getFullYear(), now.getMonth());
  if (preset === 'this_month')
    return periodOf(thisMonth, startOfMonth(now.getFullYear(), now.getMonth() + 1));
  return periodOf(startOfMonth(now.getFullYear(), now.getMonth() - 1), thisMonth);
};

const earliest = (left: Date, right: Date): Date => (left < right ? left : right);

const sameTimeYesterday = (now: Date): Date =>
  new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, now.getHours(), now.getMinutes());

const PREVIOUS_PERIODS: Readonly<Record<PeriodPreset, (now: Date) => ReportPeriod>> = {
  today: (now) => periodOf(addDays(startOfDay(now), -1), sameTimeYesterday(now)),
  last_7_days: (now) => {
    const start = addDays(startOfDay(now), 1 - DAYS_IN_WEEK);
    return periodOf(addDays(start, -DAYS_IN_WEEK), start);
  },
  this_month: (now) => {
    const thisMonth = startOfMonth(now.getFullYear(), now.getMonth());
    const lastMonth = startOfMonth(now.getFullYear(), now.getMonth() - 1);
    return periodOf(lastMonth, earliest(addDays(lastMonth, now.getDate()), thisMonth));
  },
  last_month: (now) =>
    periodOf(
      startOfMonth(now.getFullYear(), now.getMonth() - 2),
      startOfMonth(now.getFullYear(), now.getMonth() - 1),
    ),
  this_year: (now) => {
    const thisYear = startOfMonth(now.getFullYear(), 0);
    const lastYearMonthEnd = new Date(now.getFullYear() - 1, now.getMonth() + 1, 0);
    const sameDay = Math.min(now.getDate(), lastYearMonthEnd.getDate());
    const afterSameDayLastYear = new Date(now.getFullYear() - 1, now.getMonth(), sameDay + 1);
    return periodOf(startOfMonth(now.getFullYear() - 1, 0), earliest(afterSameDayLastYear, thisYear));
  },
};

export const previousPeriodOf = (preset: PeriodPreset, now: Date): ReportPeriod =>
  PREVIOUS_PERIODS[preset](now);
